import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Phone, Mail, MapPin, CheckCircle2, Users, Award, Clock, Heart,ArrowRight, } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
export default function GeneralSurgeryPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("General Surgery");



  const subspecialties = [
    {
      title: "Laparoscopic (Keyhole) Surgery",
      description: "Appendix, gallbladder, hernia & abdominal procedures with minimal scarring.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Lap-appendectomy.jpeg",
    },
    {
      title: "Gastrointestinal Surgery",
      description: "Treatment of stomach, intestines, liver & bowel conditions.",
      image: "/GastrointestinalSurgery.jpg",
    },
    {
      title: "Hernia & Abdominal Wall Surgery",
      description: "All types of open and laparoscopic hernia repairs for lasting relief.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Laser-haemorroidectomy.jpeg",
    },
    {
      title: "Anorectal Surgery",
      description: "Piles, fissures, fistula & related conditions with advanced care.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Lap-appendectomy1.jpeg",
    },
    {
      title: "Thyroid & Breast Surgery",
      description: "Removal of nodules, benign tumors, and breast lumps with precision.",
      image: "/Thyroid&BreastSurgery.jpg",
    },
    {
      title: "Trauma & Emergency Surgery",
      description: "Immediate surgical response for accidents and injuries 24/7.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Lap-Cholecystectomy.jpeg",
    },
    {
      title: "Laparoscopic Laser Surgery",
      description: "Advanced laser-assisted laparoscopic procedures for precise tissue removal with minimal bleeding and faster recovery.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/IPOM.jpeg",
    },
    {
      title: "Endovenous Laser Ablation (EVLA)",
      description: "Minimally invasive laser treatment for varicose veins, eliminating diseased veins without open surgery.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Laser-varicoce-vein.jpeg",
    },
    {
      title: "Stapler Circumcision",
      description: "Modern stapler-based circumcision technique offering reduced pain, minimal bleeding, and quicker healing.",
      image: "/stapler-circumcision-surgery.jpg",
    },
    {
      title: "Stapled Hemorrhoidectomy",
      description: "Advanced stapler procedure for hemorrhoids with less pain and faster recovery compared to conventional surgery.",
      image: "https://mamshospitals.mamscollege.com/wp-content/uploads/2026/04/Stapler-haemorroidectomy.jpeg",
    },
  ];

  const treatments = [
    "Laparoscopic Appendectomy",
    "Gallbladder Stone / Gallbladder Removal Surgery",
    "Hernia Repair (Inguinal, Umbilical, Ventral)",
    "Piles, Fissure & Fistula Treatment (Laser & Conventional)",
    "Thyroid & Breast Lump Surgery",
    "GI Surgery for Ulcers, Obstruction & Abdominal Issues",
    "Trauma & Accident Surgery",
    "Varicose Vein Treatment (Laser options available)",
  ];

  const whyChoose = [
    {
      icon: Users,
      title: "Expert Surgeons",
      description: "Expert general & laparoscopic surgeons with extensive experience",
    },
    {
      icon: Award,
      title: "Modern Operating Theatres",
      description: "Modern operating theatres with advanced anesthesia care",
    },
    {
      icon: Heart,
      title: "Minimally Invasive Surgery",
      description: "Minimally invasive surgery for quicker healing and recovery",
    },
    {
      icon: Clock,
      title: "24x7 Trauma Services",
      description: "24x7 trauma & emergency services with dedicated care units",
    },
    {
      icon: CheckCircle2,
      title: "Comprehensive Care",
      description: "Dedicated pre-operative and post-operative care units",
    },
  ];

  const articles = [
    {
      title: "Benefits of Laparoscopic Surgery: Faster Recovery, Less Pain",
      excerpt:
        "Discover how minimally invasive laparoscopic techniques reduce recovery time and post-operative discomfort.",
    },
    {
      title: "Understanding Gallbladder Stone Symptoms & Treatment Options",
      excerpt: "Learn about gallbladder stone symptoms, diagnosis, and modern surgical treatment approaches.",
    },
    {
      title: "How to Manage Piles, Fissures & Fistulas Effectively",
      excerpt: "Comprehensive guide to understanding and treating anorectal conditions with modern techniques.",
    },
    {
      title: "What to Do During a Surgical Emergency",
      excerpt: "Essential information on recognizing surgical emergencies and seeking immediate medical attention.",
    },
  ];

  const faqs = [
    {
      question: "Do you provide laparoscopic surgeries?",
      answer:
        "Yes, most of our appendix, gallbladder, and hernia surgeries are performed using minimally invasive laparoscopic techniques. This approach offers benefits like smaller incisions, less pain, faster recovery, and minimal scarring compared to traditional open surgery.",
    },
    {
      question: "Are piles and fissures treated surgically?",
      answer:
        "Yes, we offer both laser and conventional surgical procedures for piles and fissures depending on the patient's condition. Our team evaluates each case individually to recommend the most appropriate treatment option, including non-surgical management when suitable.",
    },
    {
      question: "Is emergency surgery available 24x7?",
      answer:
        "Yes, our trauma and surgical emergency team is available round-the-clock, 24 hours a day, 7 days a week. We are equipped to handle all types of surgical emergencies including accidents, acute abdominal conditions, and other urgent surgical needs.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals General Surgery Department",
    "url": "https://mamshospitals.com/general-surgery",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/GeneralSurgery.jpg",
    "description": "MAMS Hospitals provides advanced general surgery treatment in Hyderabad including laparoscopic surgery, hernia repair, gallbladder surgery, piles treatment, thyroid surgery, and trauma surgery.",
    "medicalSpecialty": "General Surgery",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Laparoscopic Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Gallbladder Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Hernia Repair Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Piles & Fissure Surgery"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Trauma & Emergency Surgery"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you provide laparoscopic surgeries?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides advanced minimally invasive laparoscopic surgeries for appendix, gallbladder, hernia, and abdominal procedures."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide laser treatment for piles and fissures?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals offers advanced laser and conventional surgical treatment for piles, fissures, and fistulas."
        }
      },
      {
        "@type": "Question",
        "name": "Is emergency surgery available 24x7?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our trauma and emergency surgical team is available 24x7 for urgent surgical care."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "General Surgery Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/general-surgery",
    "description": "Best general surgery hospital in Hyderabad offering laparoscopic surgery, hernia repair, piles treatment, gallbladder surgery, and trauma surgery.",
    "keywords": [
      "General Surgery Hospital Hyderabad",
      "Best General Surgeon Hyderabad",
      "Laparoscopic Surgery Hyderabad",
      "Hernia Surgery Hyderabad",
      "Gallbladder Surgery Hyderabad",
      "Piles Surgery Hyderabad",
      "General Surgery Hospital Bachupally"
    ]
  }
  ];
  return (
    <>
    <SEO
      title="Best General Surgery Hospital in Hyderabad | Laparoscopic Surgery | MAMS Hospitals"
      description="MAMS Hospitals offers advanced general surgery in Hyderabad including laparoscopic surgery, hernia repair, gallbladder surgery, piles treatment, thyroid surgery, and trauma care."
      keywords="general surgery hospital Hyderabad,
    best general surgeon Hyderabad,
    laparoscopic surgery Hyderabad,
    hernia surgery Hyderabad,
    gallbladder surgery Hyderabad,
    appendix surgery Hyderabad,
    piles surgery Hyderabad,
    laser piles treatment Hyderabad,
    thyroid surgery Hyderabad,
    breast lump surgery Hyderabad,
    trauma surgery Hyderabad,
    general surgery hospital Bachupally,
    MAMS Hospitals General Surgery"
      schema={schema}
      canonicalUrl="https://mamshospitals.com/departments/general-surgery"
    />

    <div className="flex flex-col min-h-screen">

      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best General Surgery Hospital in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Advanced Surgical Care with Precision & Compassion</h1>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                MAMS Hospitals stands as one of the Best General Surgery Hospitals in Hyderabad, offering safe,
                advanced, and minimally invasive surgical care for a wide range of conditions.
              </p>

             <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl z-10">
                <div className="text-3xl font-bold">98%</div>
                <div className="text-sm">Success Rate</div>
              </div>

              <img src="/GeneralSurgery.jpg"
               alt="General Surgery at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* SUBSPECIALTIES */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sub-Specialties in General Surgery</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">Comprehensive surgical expertise across multiple specializations</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all border-gray-200 hover:border-teal-500">
                <CardContent className="p-0">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img src={specialty.image} alt={specialty.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                    <p className="text-gray-600">{specialty.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENTS */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Treatments & Procedures</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">Advanced surgical procedures performed with precision and care</p>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((treatment, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-teal-600 mt-1" />
                <span className="text-gray-700">{treatment}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose MAMS Hospitals for General Surgery</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">Trusted surgical excellence with patient-centered care</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="border-gray-200 hover:shadow-lg transition">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEET SURGEON */}
                {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* ARTICLES */}
      {/* <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Recent Articles</h2>
          <p className="text-lg text-gray-600 mb-12">Stay informed about surgical care and health</p>

          <div className="grid md:grid-cols-2 gap-6">
            {articles.map((article, index) => (
              <Card key={index} className="hover:shadow-xl transition border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{article.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{article.excerpt}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Get answers to common questions about general surgery
          </p>

          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="px-6 text-left hover:text-teal-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-600">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>
                <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department </p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
